import { useQuery } from 'react-query'
import { getAdminMemberProfile } from 'services/fund'

const toman = (value: number) =>
    `${Number(value || 0).toLocaleString('fa-IR')} تومان`
const faNum = (value: number) => Number(value || 0).toLocaleString('fa-IR')
const faDigits = (value: string) =>
    value.replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)])

function Fact({
    label,
    value,
    tone,
}: {
    label: string
    value: string
    tone?: 'danger' | 'muted'
}) {
    return (
        <div className=" flex items-center justify-between gap-2 border-b border-divider py-1.5 last:border-b-0">
            <span className=" text-[11px] text-text-secondary">{label}</span>
            <span
                className={` whitespace-nowrap text-[11px] font-bold ${
                    tone === 'danger'
                        ? ' text-danger-on'
                        : tone === 'muted'
                        ? ' text-text-secondary'
                        : ' text-text-body'
                }`}
            >
                {value}
            </span>
        </div>
    )
}

/**
 * کارنامهٔ عضو on the اعطای وام screen.
 *
 * Deliberately facts only. It does not score the member, does not
 * recommend, and never blocks the grant — a manager knows their fund far
 * better than we do. What they cannot do is hold three screens' worth of
 * history in their head at the moment of deciding, which is all this fixes.
 *
 * Silent when the request fails: a broken panel must not stand between a
 * manager and granting a loan.
 */
export default function MemberProfilePanel({
    moneypoolId,
    poolshipId,
}: {
    moneypoolId: string | undefined
    poolshipId: number | undefined
}) {
    const { data, isLoading } = useQuery(
        ['admin-member-profile', moneypoolId, poolshipId],
        getAdminMemberProfile,
        { enabled: !!moneypoolId && !!poolshipId, staleTime: 60_000 }
    )

    if (!poolshipId) return null
    if (isLoading) {
        return (
            <div className=" mt-2 w-full animate-pulse rounded-xl bg-canvas p-3">
                <div className=" h-2.5 w-1/3 rounded bg-white" />
                <div className=" mt-2 h-2.5 w-2/3 rounded bg-white" />
            </div>
        )
    }
    if (!data) return null

    const { repayment, loans, portions } = data
    const hasHistory = repayment.matured_installments > 0

    return (
        <div className=" mt-2 w-full rounded-xl border border-stroke bg-white p-3 text-right">
            <div className=" mb-1 flex items-center justify-between gap-2">
                <p className=" text-xs font-bold text-brand-deep">
                    کارنامهٔ {data.member_name}
                </p>
                <span className=" text-[10px] text-text-secondary">
                    {`عضو از ${faDigits(data.joined_date.replace(/-/g, '/'))}`}
                </span>
            </div>

            {/* The headline is the repayment RECORD, not today's snapshot.
                «قسط معوقی ندارد» in green above «۰٪ به‌موقع، میانگین ۸۳ روز
                تاخیر» is the same lie the on-time-rate denominator was fixed
                to avoid: someone who eventually paid every instalment months
                late is current, and is not a punctual borrower. Green is
                reserved for a genuinely clean record. */}
            {repayment.overdue_count > 0 ? (
                <div className=" mb-2 rounded-lg bg-danger-soft px-2.5 py-1.5">
                    <p className=" text-[11px] font-bold text-danger-on">
                        {`${faNum(
                            repayment.overdue_count
                        )} قسط معوق — ${toman(repayment.overdue_amount)}`}
                    </p>
                    <p className=" mt-0.5 text-[10px] text-danger-on">
                        {`تا ${faNum(repayment.max_days_overdue)} روز تاخیر`}
                    </p>
                </div>
            ) : hasHistory && repayment.late_installments > 0 ? (
                <div className=" mb-2 rounded-lg bg-warning-soft px-2.5 py-1.5">
                    <p className=" text-[11px] font-bold text-warning-on">
                        الان قسط معوقی ندارد
                    </p>
                    <p className=" mt-0.5 text-[10px] text-warning-on">
                        {`ولی ${faNum(
                            repayment.late_installments
                        )} قسط از ${faNum(
                            repayment.matured_installments
                        )} قسط را با تاخیر پرداخت کرده`}
                    </p>
                </div>
            ) : hasHistory ? (
                <div className=" mb-2 rounded-lg bg-success-2 px-2.5 py-1.5">
                    <p className=" text-[11px] font-bold text-success-on">
                        {`همهٔ ${faNum(
                            repayment.matured_installments
                        )} قسط را به‌موقع پرداخت کرده`}
                    </p>
                </div>
            ) : (
                <div className=" mb-2 rounded-lg bg-canvas px-2.5 py-1.5">
                    <p className=" text-[11px] text-text-secondary">
                        تا امروز قسطی سررسید نشده — سابقهٔ بازپرداختی ندارد
                    </p>
                </div>
            )}

            <Fact label="سرمایهٔ فعلی در صندوق" value={toman(data.capital)} />
            <Fact
                label="مجموع واریز / برداشت"
                value={`${toman(data.deposited)} / ${toman(data.withdrawn)}`}
                tone="muted"
            />
            <Fact
                label="حق عضویت پرداخت‌شده"
                value={`${faNum(portions.paid_count)} از ${faNum(
                    portions.due_count
                )} دوره`}
            />
            {portions.unpaid_amount > 0 && (
                <Fact
                    label="حق عضویت معوق"
                    value={toman(portions.unpaid_amount)}
                    tone="danger"
                />
            )}
            <Fact
                label="وام‌های گرفته‌شده"
                value={
                    loans.count > 0
                        ? `${faNum(loans.count)} وام — ${toman(
                              loans.total_amount
                          )}`
                        : 'ندارد'
                }
            />
            {loans.outstanding > 0 && (
                <Fact
                    label="مانده وام فعلی"
                    value={toman(loans.outstanding)}
                />
            )}
            {hasHistory && (
                <>
                    <Fact
                        label="اقساط پرداخت‌شده به‌موقع"
                        value={`${faNum(
                            repayment.on_time_rate ?? 0
                        )}٪ از ${faNum(
                            repayment.matured_installments
                        )} قسط سررسیدشده`}
                    />
                    {repayment.late_installments > 0 && (
                        <Fact
                            label="میانگین تاخیر (وقتی دیر کرده)"
                            value={`${faNum(repayment.mean_days_late)} روز`}
                            tone="muted"
                        />
                    )}
                </>
            )}
        </div>
    )
}
